import { useEffect, useState } from "react";
import { flowRun, type FlowStep, type Json, type QpRiskOptions } from "../../api";
import { Cta } from "./PhoneFrame";

// Shared props for every step screen. `onDone` re-polls status and advances.
export interface StepProps {
  runId: number;
  step: FlowStep;
  onDone: () => void;
}

// QP labels come as { labelI18n: { en_GB, nb_NO }, label }. Prefer English.
function lbl(o: unknown, fallback = ""): string {
  const r = o as { labelI18n?: Record<string, string>; label?: string } | null;
  return r?.labelI18n?.en_GB ?? r?.label ?? fallback;
}

function Loading() {
  return (
    <div className="iapp-center">
      <div className="iapp-spinner" />
      <span>Loading…</span>
    </div>
  );
}

// Money input with Norwegian space thousand-separators (1 000 000). Stores the
// raw digit string; formats for display. `value` is digits-only.
function digitsOnly(s: string): string {
  return s.replace(/\D/g, "");
}
export function formatMoney(digits: string): string {
  const n = digitsOnly(digits);
  return n ? n.replace(/\B(?=(\d{3})+(?!\d))/g, " ") : "";
}
function MoneyInput({
  value,
  onChange,
  placeholder = "0",
  autoFocus,
}: {
  value: string;
  onChange: (rawDigits: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
}) {
  return (
    <div className="inp">
      <span>kr</span>
      <input
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder={placeholder}
        value={formatMoney(value)}
        onChange={(e) => onChange(digitsOnly(e.target.value))}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={autoFocus}
      />
    </div>
  );
}

// ---- Financial situation (verified path) -----------------------------------
// Grouped amount inputs built from the tenant's field defs. We send only the
// amounts the investor actually entered (per the endpoint's contract).

interface FinField {
  id: string;
  type?: string;
  label?: string;
  labelI18n?: Record<string, string>;
  helpText?: string;
  hasMonthlyPayment?: boolean;
}
interface FinFields {
  incomeFields?: FinField[];
  assetFields?: FinField[];
  liabilityFields?: FinField[];
  commitmentFields?: FinField[];
}

// Hybrid presentation: QP owns the fields (ids/labels/help), Andersen owns the
// pacing — one short screen per group instead of one 28-field wall. Curated to
// the first PRIMARY_COUNT fields (by QP's order) with a "show more" reveal for
// the long tail. Nothing is hidden permanently and no field id is hardcoded.
const FIN_GROUPS: {
  key: keyof FinFields;
  entryKey: string;
  title: string;
  lede: string;
}[] = [
  {
    key: "incomeFields",
    entryKey: "incomeEntries",
    title: "Your income",
    lede: "What comes in. Enter what applies — leave the rest blank.",
  },
  {
    key: "assetFields",
    entryKey: "assetEntries",
    title: "What you own",
    lede: "Savings, investments, property and other assets.",
  },
  {
    key: "liabilityFields",
    entryKey: "liabilityEntries",
    title: "Debt",
    lede: "Mortgage, loans and any other debts.",
  },
  {
    key: "commitmentFields",
    entryKey: "commitmentEntries",
    title: "Your regular costs",
    lede: "Ongoing commitments like housing and living costs.",
  },
];
const PRIMARY_COUNT = 5;

// Curated income fields for the demo: show only these three, in this order,
// relabeled for a clean retail feel. Mapped to QP's real field ids so the
// amounts we submit still carry the correct typeId. (QP returns 7 income
// fields; the rest — self-employment, pension, government benefits, other —
// are hidden here.)
const INCOME_CURATION: { id: string; label: string }[] = [
  { id: "employment", label: "Salary" },
  { id: "rental", label: "Rental income" },
  { id: "other_income", label: "Other income" },
];
// "What you own": Cash / Investments / Real Estate / Other. (QP has no vehicle
// asset field, so a car isn't shown here — its value can go under "Other".)
const ASSET_CURATION: { id: string; label: string }[] = [
  { id: "cash_savings", label: "Cash" },
  { id: "stocks_funds", label: "Investments" },
  { id: "primary_residence", label: "Real Estate" },
  { id: "other_assets", label: "Other" },
];
// "Debt": Mortgage / Car loan / Student loan.
const LIABILITY_CURATION: { id: string; label: string }[] = [
  { id: "mortgage", label: "Mortgage" },
  { id: "car_loan", label: "Car loan" },
  { id: "student_loans", label: "Student loan" },
];
// "Your regular costs": Housing Costs / Other Costs.
const COMMITMENT_CURATION: { id: string; label: string }[] = [
  { id: "housing", label: "Housing Costs" },
  { id: "other_fixed", label: "Other Costs" },
];

// Reduce a QP field group to a curated subset, in order, with clean labels —
// keeping each field's real QP id so submitted amounts carry the right typeId.
function curateFields(
  all: FinField[] | undefined,
  curation: { id: string; label: string }[],
): FinField[] {
  const out: FinField[] = [];
  for (const c of curation) {
    const src = all?.find((f) => f.id === c.id);
    if (src) out.push({ ...src, label: c.label, labelI18n: undefined, helpText: undefined });
  }
  return out;
}

export function FinancialSituationStep({ runId, onDone }: StepProps) {
  const [fields, setFields] = useState<FinFields | null>(null);
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const [idx, setIdx] = useState(0); // which sub-screen
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    flowRun
      .step(runId, "financial-situation")
      .then((d) => {
        const raw = (d.step as { fields?: FinFields }).fields ?? {};
        // Curate income + assets to the demo fields (relabeled, QP ids
        // preserved). Liabilities and costs are left as QP returns them.
        if (raw.incomeFields) raw.incomeFields = curateFields(raw.incomeFields, INCOME_CURATION);
        if (raw.assetFields) raw.assetFields = curateFields(raw.assetFields, ASSET_CURATION);
        if (raw.liabilityFields)
          raw.liabilityFields = curateFields(raw.liabilityFields, LIABILITY_CURATION);
        if (raw.commitmentFields)
          raw.commitmentFields = curateFields(raw.commitmentFields, COMMITMENT_CURATION);
        setFields(raw);
      })
      .catch((e) => setErr(e.message));
  }, [runId]);

  if (err && !fields) return <div className="iapp-view"><div className="iapp-error">{err}</div></div>;
  if (!fields) return <Loading />;

  // Only groups QP actually returns fields for become screens.
  const pages = FIN_GROUPS.filter((g) => (fields[g.key] ?? []).length > 0);
  if (pages.length === 0) {
    // Nothing to collect — submit an empty baseline and move on.
    return (
      <>
        <div className="iapp-view">
          <h2>Your finances</h2>
          <p className="lede">Nothing to add here.</p>
        </div>
        <Cta
          label={busy ? "Saving…" : "Continue"}
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await flowRun.submitStep(runId, "financial-situation", { currency: "NOK" });
              onDone();
            } catch (e) {
              setErr((e as Error).message);
              setBusy(false);
            }
          }}
        />
      </>
    );
  }

  const page = pages[Math.min(idx, pages.length - 1)]!;
  const all = fields[page.key] ?? [];
  const showAll = expanded[page.key];
  const visible = showAll ? all : all.slice(0, PRIMARY_COUNT);
  const hidden = all.length - visible.length;
  const isLast = idx >= pages.length - 1;
  const set = (id: string, v: string) => setAmounts((a) => ({ ...a, [id]: v }));

  async function finish() {
    setBusy(true);
    setErr(null);
    // Single PUT with every entered amount across all groups. QP entry shape:
    // { id (unique per entry), typeId (field id), label, amount }.
    const body: Json = { currency: "NOK" };
    for (const g of FIN_GROUPS) {
      body[g.entryKey] = (fields![g.key] ?? [])
        .map((f) => ({
          id: crypto.randomUUID(),
          typeId: f.id,
          label: lbl(f, f.label),
          amount: Number(amounts[f.id] || 0),
        }))
        .filter((e) => e.amount > 0);
    }
    try {
      await flowRun.submitStep(runId, "financial-situation", body);
      onDone();
    } catch (e) {
      setErr((e as Error).message);
      setBusy(false);
    }
  }

  return (
    <>
      <div className="iapp-view">
        <div className="iapp-substeps">
          {pages.map((g, i) => (
            <span key={g.key} className={i === idx ? "on" : i < idx ? "done" : ""} />
          ))}
        </div>
        {idx > 0 && (
          <button className="iapp-back-inline" type="button" onClick={() => setIdx(idx - 1)}>
            ‹ Back
          </button>
        )}
        <h2>{page.title}</h2>
        <p className="lede">{page.lede}</p>
        <div className="iapp-group">
          {visible.map((f) => (
            <div className="iapp-amount" key={f.id}>
              <div className="lab">
                {lbl(f, f.label)}
                {f.helpText && <small>{f.helpText}</small>}
              </div>
              <MoneyInput value={amounts[f.id] ?? ""} onChange={(v) => set(f.id, v)} />
            </div>
          ))}
        </div>
        {hidden > 0 && (
          <button
            className="iapp-morebtn"
            type="button"
            onClick={() => setExpanded((s) => ({ ...s, [page.key]: true }))}
          >
            + Show {hidden} more
          </button>
        )}
        {err && <div className="iapp-error" style={{ marginTop: 16 }}>{err}</div>}
      </div>
      <Cta
        label={busy ? "Saving…" : isLast ? "Continue" : "Next"}
        onClick={() => (isLast ? finish() : setIdx(idx + 1))}
        disabled={busy}
      />
    </>
  );
}

// ---- Risk assessment (verified path → produces the advice) -----------------
// One question per screen: timeframe → risk appetite → the "20% drop" reaction.
// QP's three risk levels are Low / Medium / Growth; we present them as
// Low / Medium / High (real QP ids preserved). Relabel by id, fall back to
// QP's own label if the tenant's ids ever change.
const RISK_LEVEL_LABELS: Record<string, string> = {
  "c30a1ba7-3986-4983-988c-44518205b3c3": "Low",
  "b73211d1-e7f9-45f5-b34d-4f7132d80bac": "Medium",
  "b93c41f3-2bd0-4611-9956-de3041c49086": "High",
};

export function RiskStep({ runId, step, onDone }: StepProps) {
  const goalId = step.goalId ?? "";
  const [opts, setOpts] = useState<QpRiskOptions | null>(null);
  const [horizon, setHorizon] = useState("");
  const [level, setLevel] = useState("");
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [idx, setIdx] = useState(0); // which question screen
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!goalId) {
      setErr("This risk step has no goal attached.");
      return;
    }
    flowRun
      .riskOptions(runId, goalId)
      .then((d) => setOpts(d.options))
      .catch((e) => setErr(e.message));
  }, [runId, goalId]);

  if (err && !opts) return <div className="iapp-view"><div className="iapp-error">{err}</div></div>;
  if (!opts) return <Loading />;

  // One screen per question, in order: timeframe, risk appetite, adjustments.
  interface RiskScreen {
    key: string;
    title: string;
    lede: string;
    options: { id: string; label: string }[];
    value: string;
    set: (id: string) => void;
  }
  const screens: RiskScreen[] = [
    {
      key: "horizon",
      title: "When will you need the money?",
      lede: "Your timeframe shapes how much risk suits you.",
      options: opts.timeHorizons.map((t) => ({ id: t.id, label: t.label })),
      value: horizon,
      set: setHorizon,
    },
    {
      key: "level",
      title: "What risk do you want?",
      lede: "How much risk do you want to take with this money?",
      options: opts.riskLevels.map((r) => ({ id: r.id, label: RISK_LEVEL_LABELS[r.id] ?? r.label })),
      value: level,
      set: setLevel,
    },
    ...opts.adjustmentQuestions.map((q) => ({
      key: q.id,
      title: q.label,
      lede: "There's no right answer — it just helps us match you.",
      options: q.answers.map((a) => ({ id: a.id, label: a.label })),
      value: answers[q.id] ?? "",
      set: (id: string) => setAnswers((s) => ({ ...s, [q.id]: id })),
    })),
  ];

  const cur = screens[Math.min(idx, screens.length - 1)]!;
  const isLast = idx >= screens.length - 1;

  async function submit() {
    setBusy(true);
    setErr(null);
    try {
      await flowRun.submitRisk(runId, goalId, {
        timeHorizonId: horizon,
        riskLevelId: level,
        adjustmentAnswers: answers,
      });
      onDone();
    } catch (e) {
      setErr((e as Error).message);
      setBusy(false);
    }
  }

  return (
    <>
      <div className="iapp-view">
        <div className="iapp-substeps">
          {screens.map((s, i) => (
            <span key={s.key} className={i === idx ? "on" : i < idx ? "done" : ""} />
          ))}
        </div>
        {idx > 0 && (
          <button className="iapp-back-inline" type="button" onClick={() => setIdx(idx - 1)}>
            ‹ Back
          </button>
        )}
        <h2>{cur.title}</h2>
        <p className="lede">{cur.lede}</p>
        {cur.options.map((o) => (
          <button
            key={o.id}
            type="button"
            className={cur.value === o.id ? "iapp-choice sel" : "iapp-choice"}
            onClick={() => cur.set(o.id)}
          >
            <span className="radio" />
            <span className="body">{o.label}</span>
          </button>
        ))}
        {err && <div className="iapp-error" style={{ marginTop: 16 }}>{err}</div>}
      </div>
      <Cta
        label={busy ? "Matching your portfolio…" : isLast ? "See my portfolio" : "Next"}
        onClick={() => (isLast ? submit() : setIdx(idx + 1))}
        disabled={!cur.value || busy}
      />
    </>
  );
}

// ---- Product assessment — QUIZ mode (best-effort) --------------------------
// The tenant runs product assessment as a quiz: /options gives modes but
// questions:null, so we start the quiz to get questions. Body shapes for
// submit are not fully specified in the OpenAPI — failures are surfaced and
// logged to docs/quantfolio-api-notes.md.

export function ProductAssessmentStep({ runId, onDone }: StepProps) {
  const [quiz, setQuiz] = useState<Json | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // quiz/start isn't exposed as a typed client method yet; use step render to
    // read options, then start via the generic step endpoint if available.
    flowRun
      .step(runId, "product-assessment")
      .then((d) => setQuiz(d.step))
      .catch((e) => setErr(e.message));
  }, [runId]);

  if (err && !quiz) return <div className="iapp-view"><div className="iapp-error">{err}</div></div>;
  if (!quiz) return <Loading />;

  const options = (quiz as { options?: unknown[] }).options ?? [];
  const questions = options.flatMap((o) => {
    const q = (o as { questions?: unknown[] }).questions;
    return Array.isArray(q) ? q : [];
  }) as { id?: string; label?: string; answers?: { id?: string; label?: string }[] }[];

  async function submit() {
    setBusy(true);
    setErr(null);
    try {
      await flowRun.submitStep(runId, "product-assessment", { answers });
      onDone();
    } catch (e) {
      setErr((e as Error).message);
      setBusy(false);
    }
  }

  return (
    <>
      <div className="iapp-view">
        <h2>Your investing know-how</h2>
        <p className="lede">
          A short check that these products are a fit for your experience.
        </p>
        {questions.length === 0 ? (
          <div className="iapp-note">
            This quiz is configured on the Quantfolio side and will run here once
            its questions are wired. You can continue for now.
          </div>
        ) : (
          questions.map((q, i) => (
            <div key={q.id ?? i}>
              <div className="iapp-section-label">{lbl(q, q.label)}</div>
              {(q.answers ?? []).map((a, j) => (
                <button
                  key={a.id ?? j}
                  type="button"
                  className={answers[q.id ?? ""] === a.id ? "iapp-choice sel" : "iapp-choice"}
                  onClick={() => setAnswers((s) => ({ ...s, [q.id ?? ""]: a.id ?? "" }))}
                >
                  <span className="radio" />
                  <span className="body">{lbl(a, a.label)}</span>
                </button>
              ))}
            </div>
          ))
        )}
        {err && <div className="iapp-error" style={{ marginTop: 16 }}>{err}</div>}
      </div>
      <Cta label={busy ? "Saving…" : "Continue"} onClick={submit} disabled={busy} />
    </>
  );
}

// ---- Sustainability preferences (best-effort) ------------------------------

export function SustainabilityStep({ runId, onDone }: StepProps) {
  // Retail framing: a simple "does this matter to you?" question. "Not a
  // priority" submits { important: false }, which cleanly completes the step
  // (QP: important=false clears preferences and the gate governs). "Yes" sends
  // the disclosed preference bars.
  const [config, setConfig] = useState<Json | null>(null);
  const [care, setCare] = useState<"" | "no" | "yes">("");
  const [picks, setPicks] = useState<Record<string, string>>({});
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    flowRun
      .step(runId, "sustainability")
      .then((d) => setConfig((d.step as { options?: Json }).options ?? {}))
      .catch((e) => setErr(e.message));
  }, [runId]);

  if (err && !config) return <div className="iapp-view"><div className="iapp-error">{err}</div></div>;
  if (!config) return <Loading />;

  const bars =
    ((config as { config?: { bars?: unknown[] } }).config?.bars ?? []) as {
      key?: string;
      steps?: { key?: string; labelI18n?: Record<string, string> }[];
      labelI18n?: Record<string, string>;
    }[];

  async function submit() {
    setBusy(true);
    setErr(null);
    const body: Json =
      care === "yes"
        ? { important: true, preferences: { bars: picks } }
        : { important: false };
    try {
      await flowRun.submitStep(runId, "sustainability", body);
      onDone();
    } catch (e) {
      setErr((e as Error).message);
      setBusy(false);
    }
  }

  const ready = care === "no" || (care === "yes" && bars.every((b) => picks[b.key ?? ""]));

  return (
    <>
      <div className="iapp-view">
        <h2>What you care about</h2>
        <p className="lede">
          Should sustainability shape your investments? There's no wrong answer.
        </p>
        <button
          type="button"
          className={care === "yes" ? "iapp-choice sel" : "iapp-choice"}
          onClick={() => setCare("yes")}
        >
          <span className="radio" />
          <span className="body">
            Yes, it matters to me
            <span className="sub">We'll factor in your sustainability preferences</span>
          </span>
        </button>
        <button
          type="button"
          className={care === "no" ? "iapp-choice sel" : "iapp-choice"}
          onClick={() => setCare("no")}
        >
          <span className="radio" />
          <span className="body">
            Not a priority right now
            <span className="sub">Focus purely on risk and return</span>
          </span>
        </button>

        {care === "yes" &&
          bars.map((b, i) => (
            <div key={b.key ?? i}>
              <div className="iapp-section-label">{lbl(b)}</div>
              {(b.steps ?? []).map((s, j) => (
                <button
                  key={s.key ?? j}
                  type="button"
                  className={picks[b.key ?? ""] === s.key ? "iapp-choice sel" : "iapp-choice"}
                  onClick={() => setPicks((p) => ({ ...p, [b.key ?? ""]: s.key ?? "" }))}
                >
                  <span className="radio" />
                  <span className="body">{lbl(s)}</span>
                </button>
              ))}
            </div>
          ))}
        {err && <div className="iapp-error" style={{ marginTop: 16 }}>{err}</div>}
      </div>
      <Cta label={busy ? "Saving…" : "Continue"} onClick={submit} disabled={!ready || busy} />
    </>
  );
}

// ---- Goal purpose (goal_input) ---------------------------------------------
// Retail "what are you investing for?" — the investor picks a goal type, which
// sets the goal's name + goalType. QP has the real goal types configured on the
// Investment Advice flow's portfolio collection but does NOT expose them over
// the API, so we HARDCODE the tenant's ids + icons below. Sending a real
// goalType id (not "other") completes the goal_input "Purpose" step — verified
// against the live API. See docs/quantfolio-api-notes.md #6.
//
// Icons are the tenant's own goal-type SVGs (currentColor so they tint with the
// card's selected state). Ordered by QP's `order` field.
const icon = (inner: string) =>
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${inner}</svg>`;

const GOAL_TYPES: { id: string; label: string; icon: string }[] = [
  {
    id: "82018c58-fec7-42a3-bb4c-0d4c9664594a",
    label: "Grow my wealth",
    icon: icon('<path d="M4 16.5 9.5 11l3.5 3.5L21 7"/><path d="M15.5 7H21v5.5"/><path d="M3 20.5h18"/>'),
  },
  {
    id: "d543923a-c108-43c9-8ac5-9012b12c896e",
    label: "Retirement",
    icon: icon('<path d="M6 3h12"/><path d="M6 21h12"/><path d="M8 3v4.2c0 1.4 1 2.3 2.2 3.1L12 11.5l1.8-1.2C15 9.5 16 8.6 16 7.2V3"/><path d="M8 21v-4.2c0-1.4 1-2.3 2.2-3.1L12 12.5l1.8 1.2C15 14.5 16 15.4 16 16.8V21"/>'),
  },
  {
    id: "1fc3017c-08bc-49e4-a04a-7a5b22d52b0c",
    label: "Buffer",
    icon: icon('<path d="M2 12a10 10 0 0 1 20 0Z"/><path d="M12 12v7a2.5 2.5 0 0 0 5 0"/>'),
  },
  {
    id: "709bedff-d6bf-42a5-bfe2-7d3ef7298ea1",
    label: "Planned major purchase",
    icon: icon('<path d="M5 8h14l-1 12H6Z"/><path d="M9 8V6a3 3 0 0 1 6 0v2"/>'),
  },
  {
    id: "642d4056-3d48-4581-b5f7-beca79917738",
    label: "Home purchase",
    icon: icon('<path d="M3 10.5 12 3l9 7.5"/><path d="M5.5 9.5V20a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1V9.5"/><path d="M9.5 21v-6h5v6"/>'),
  },
  {
    id: "4f6750ab-d4da-45be-ba98-05993f3135be",
    label: "Saving for children",
    icon: icon('<path d="M12 4 2 9l10 5 10-5Z"/><path d="M6 11.2V16c0 1.7 2.7 3 6 3s6-1.3 6-3v-4.8"/><path d="M21 9.5v5"/>'),
  },
];

export function GoalPurposeStep({ runId, step, onDone }: StepProps) {
  const goalId = step.goalId ?? "";
  const useTypes = GOAL_TYPES.length > 0;
  const [typeId, setTypeId] = useState<string>("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const presets = ["Retirement", "A home", "My children", "Building wealth", "A rainy day"];
  const selectedType = GOAL_TYPES.find((t) => t.id === typeId);
  const ready = useTypes ? !!typeId : true;

  async function submit() {
    setBusy(true);
    setErr(null);
    try {
      const body = useTypes
        ? { name: selectedType?.label || "My goal", goalType: typeId }
        : { name: name || "My goal", goalType: "other" };
      await flowRun.patchGoal(runId, goalId, body);
      onDone();
    } catch (e) {
      setErr((e as Error).message);
      setBusy(false);
    }
  }

  return (
    <>
      <div className="iapp-view">
        <h2>What's this for?</h2>
        <p className="lede">
          {useTypes
            ? "Pick what you're investing for — it shapes your plan."
            : "Give your goal a name — it helps keep you on track."}
        </p>

        {useTypes ? (
          <div className="iapp-goalgrid">
            {GOAL_TYPES.map((t) => (
              <button
                key={t.id}
                type="button"
                className={typeId === t.id ? "iapp-goalcard sel" : "iapp-goalcard"}
                onClick={() => setTypeId(t.id)}
              >
                <span className="ic" dangerouslySetInnerHTML={{ __html: t.icon }} />
                <span className="lab">{t.label}</span>
              </button>
            ))}
          </div>
        ) : (
          <>
            <div className="iapp-group" style={{ padding: 16 }}>
              <input
                className="iapp-textinput"
                placeholder="e.g. Retirement"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 }}>
              {presets.map((p) => (
                <button
                  key={p}
                  type="button"
                  className={name === p ? "iapp-chip sel" : "iapp-chip"}
                  onClick={() => setName(p)}
                >
                  {p}
                </button>
              ))}
            </div>
          </>
        )}

        {err && <div className="iapp-error" style={{ marginTop: 16 }}>{err}</div>}
      </div>
      <Cta label={busy ? "Saving…" : "Continue"} onClick={submit} disabled={!ready || busy} />
    </>
  );
}

// ---- Investment amount (portfolio_input) -----------------------------------

export function PortfolioAmountStep({ runId, step, onDone }: StepProps) {
  const goalId = step.goalId ?? "";
  const [portfolioId, setPortfolioId] = useState<string | null>(null);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    flowRun
      .portfolios(runId, goalId)
      .then((d) => {
        const model = d.portfolios.find((p) => p.portfolioType === "model") ?? d.portfolios[0];
        setPortfolioId(model?.id ?? null);
      })
      .catch((e) => setErr(e.message));
  }, [runId, goalId]);

  const quick = [100000, 250000, 500000, 1000000];

  async function submit() {
    if (!portfolioId) return;
    setBusy(true);
    setErr(null);
    try {
      await flowRun.patchPortfolio(runId, goalId, portfolioId, {
        scheduleType: "one_time",
        oneTimeAmount: Number(amount),
      });
      onDone();
    } catch (e) {
      setErr((e as Error).message);
      setBusy(false);
    }
  }

  const valid = Number(amount) > 0 && portfolioId;

  return (
    <>
      <div className="iapp-view">
        <h2>How much to invest?</h2>
        <p className="lede">A one-off amount to get your plan started.</p>
        <div className="iapp-group" style={{ padding: 16 }}>
          <div className="iapp-amount" style={{ borderBottom: "none" }}>
            <div className="lab">One-time investment</div>
            <MoneyInput value={amount} onChange={setAmount} autoFocus />
          </div>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 }}>
          {quick.map((q) => (
            <button
              key={q}
              type="button"
              className={Number(amount) === q ? "iapp-chip sel" : "iapp-chip"}
              onClick={() => setAmount(String(q))}
            >
              {formatMoney(String(q))} kr
            </button>
          ))}
        </div>
        {err && <div className="iapp-error" style={{ marginTop: 16 }}>{err}</div>}
      </div>
      <Cta label={busy ? "Saving…" : "Continue"} onClick={submit} disabled={!valid || busy} />
    </>
  );
}

// ---- Flow fields (kind: "field" — advice metadata dropdowns) ---------------

interface FlowFieldDef {
  id: string;
  name: string;
  type: string;
  required: boolean;
  options?: { optionId: string; label: string }[];
}

// These flow fields are the advisor-facing "Advice information" page (where the
// meeting was held, who initiated it). They're the FIRST steps QP asks for, but
// they don't belong in a self-service investor app — so we answer them from
// fixed defaults and skip the screen entirely. Keyed by QP flowFieldId →
// optionId (verified against the live flow: PUT /fields completes both).
const FLOW_FIELD_DEFAULTS: Record<string, string> = {
  // "Where was the meeting held?" → "Digital"
  "a260bd85-acc8-4dee-8824-a7945981bce7": "695ae004-ebcf-429c-97a0-fa0854e213b5",
  // "Who initiated the meeting" → "The client"
  "b74e9114-853c-417e-9ba8-5cfccfd1712b": "7ced6417-8dd3-4773-a401-4a1fc00d00ec",
};

export function FlowFieldsStep({ runId, onDone }: StepProps) {
  const [defs, setDefs] = useState<FlowFieldDef[] | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [skipping, setSkipping] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    flowRun
      .step(runId, "fields")
      .then((d) => {
        const step = d.step as { defs?: { fields?: FlowFieldDef[] } };
        const list = step.defs?.fields ?? [];
        setDefs(list);
        // Pre-fill any field we have a fixed default for.
        const pre: Record<string, string> = {};
        for (const f of list) {
          const v = FLOW_FIELD_DEFAULTS[f.id];
          if (v) pre[f.id] = v;
        }
        setValues(pre);
        // If our defaults cover every required field, submit them and skip the
        // screen — the investor never sees this advisor-oriented page.
        const req = list.filter((f) => f.required);
        if (req.length > 0 && req.every((f) => pre[f.id])) {
          setSkipping(true);
          const payload = list
            .filter((f) => pre[f.id])
            .map((f) => ({ flowFieldId: f.id, value: pre[f.id]! }));
          flowRun
            .submitStep(runId, "fields", { values: payload })
            .then(onDone)
            .catch((e) => {
              setErr((e as Error).message);
              setSkipping(false);
            });
        }
      })
      .catch((e) => setErr(e.message));
    // Mount-once: onDone is a fresh closure each parent render; including it
    // would re-run this effect (and risk a double submit).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId]);

  if (err && !defs) return <div className="iapp-view"><div className="iapp-error">{err}</div></div>;
  if (!defs || skipping) return <Loading />;

  const required = defs.filter((d) => d.required);
  const ready = required.every((d) => values[d.id]);

  async function submit() {
    setBusy(true);
    setErr(null);
    const payload = defs!
      .filter((d) => values[d.id])
      .map((d) => ({ flowFieldId: d.id, value: values[d.id] }));
    try {
      await flowRun.submitStep(runId, "fields", { values: payload });
      onDone();
    } catch (e) {
      setErr((e as Error).message);
      setBusy(false);
    }
  }

  return (
    <>
      <div className="iapp-view">
        <h2>A few details</h2>
        <p className="lede">This keeps your advice record complete.</p>
        {defs.map((f) => (
          <div key={f.id}>
            <div className="iapp-section-label">{f.name}</div>
            {f.options && f.options.length > 0 ? (
              f.options.map((o) => (
                <button
                  key={o.optionId}
                  type="button"
                  className={values[f.id] === o.optionId ? "iapp-choice sel" : "iapp-choice"}
                  onClick={() => setValues((v) => ({ ...v, [f.id]: o.optionId }))}
                >
                  <span className="radio" />
                  <span className="body">{o.label}</span>
                </button>
              ))
            ) : (
              <div className="iapp-group" style={{ padding: 16 }}>
                <input
                  className="iapp-textinput"
                  value={values[f.id] ?? ""}
                  onChange={(e) => setValues((v) => ({ ...v, [f.id]: e.target.value }))}
                />
              </div>
            )}
          </div>
        ))}
        {err && <div className="iapp-error" style={{ marginTop: 16 }}>{err}</div>}
      </div>
      <Cta label={busy ? "Saving…" : "Continue"} onClick={submit} disabled={!ready || busy} />
    </>
  );
}

// ---- Generic fallback ------------------------------------------------------
// Any open step we don't have a bespoke renderer for. Shows what QP is asking
// and lets the investor continue (re-polls status in case it auto-resolved).

export function GenericStep({ onDone, step }: StepProps) {
  return (
    <>
      <div className="iapp-view">
        <h2>{step.pageName ?? "One more thing"}</h2>
        <p className="lede">
          This part of your plan is handled in Quantfolio. Continue when you're
          ready.
        </p>
        <div className="iapp-note">
          Step: <strong>{step.componentType ?? step.kind}</strong>
          {step.fieldName ? ` — ${step.fieldName}` : ""}
        </div>
      </div>
      <Cta label="Continue" onClick={onDone} />
    </>
  );
}
